import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// Make sure to install: npm install react-signature-canvas
import SignaturePad from 'react-signature-canvas';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import PaymentPortal from './ClientPaymentsPage';
import { v4 as uuidv4 } from 'uuid'

// Generate a filled PDF preview (no signature) and return object URL
async function generatePreview(templatePath, values) {
  const existingBytes = await fetch(templatePath).then(r => r.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingBytes);
  const form = pdfDoc.getForm();

  // Fill form fields
  form.getTextField('case_id').setText(values.case_id);
  form.getTextField('pre_filing_fees').setText(values.pre_filing_fees.toFixed(2));
  form.getTextField('filing_fee_amount').setText(values.filing_fee_amount.toFixed(2));
  form.getTextField('down_payment').setText(
    values.down_payment != null ? values.down_payment.toFixed(2) : ''
  );
  form.getTextField('Date').setText(values.date_signed);
  form.getTextField('debtor_full_name').setText(values.debtor_full_name);
  if (values.post_filing_fees != null && form.getFieldMaybe && form.getFieldMaybe('post_filing_fees')) {
    form.getTextField('post_filing_fees').setText(values.post_filing_fees.toFixed(2));
  }

  form.flatten();
  const bytes = await pdfDoc.save();
  const blob = new Blob([bytes], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}

// Helper to fill and download PDF template with values and signature
const logoUrl = '/images/logo.png';
async function fillAndDownloadTemplate(
  templatePath,
  values,
  signaturePngBase64,
  meta
) {
  // Fetch the blank PDF template
  const existingPdfBytes = await fetch(templatePath).then(r => r.arrayBuffer());
  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const form = pdfDoc.getForm();

  // Fill text fields (must match your Acrobat field names)
  form.getTextField('case_id').setText(values.case_id);
  form.getTextField('pre_filing_fees').setText(values.pre_filing_fees.toFixed(2));
  form.getTextField('filing_fee_amount').setText(values.filing_fee_amount.toFixed(2));
  form.getTextField('down_payment').setText(
    values.down_payment != null ? values.down_payment.toFixed(2) : ''
  );
  form.getTextField('Date').setText(values.date_signed);
  form.getTextField('debtor_full_name').setText(values.debtor_full_name);
  if (values.post_filing_fees != null && form.getFieldMaybe && form.getFieldMaybe('post_filing_fees')) {
    form.getTextField('post_filing_fees').setText(values.post_filing_fees.toFixed(2));
  }

  // Embed and draw the signature PNG
  // Embed and draw the signature PNG (scale-to-fit the actual PDF field)
  const pngImage = await pdfDoc.embedPng(signaturePngBase64);
  const sigField = form.getButton('client_signature');
  const widgets = sigField.acroField.getWidgets();
  // Select the last widget to match the signature field on the final page
  const widget = widgets[widgets.length - 1];
  const rect = widget.getRectangle();

  // Draw on the correct page
  const pages = pdfDoc.getPages();
  const lastPage = pages[pages.length - 1];

  // pdf-lib gives us the image's intrinsic size. Mobile devices often produce
  // much higher resolution data URLs, so we must scale to the field rectangle.
  const img = pngImage.size(); // { width, height }

  // Minimal padding so the signature fills the field better (but still avoids clipping)
  const padding = 0.5;
  const targetW = Math.max(1, rect.width - padding * 2);
  const targetH = Math.max(1, rect.height - padding * 2);

  // Scale signature to fit inside the signature field while preserving aspect ratio
  const scale = Math.min(targetW / img.width, targetH / img.height);
  const drawW = img.width * scale;
  const drawH = img.height * scale;

  // Center the signature in the field (account for padding)
  const drawX = rect.x + (rect.width - drawW) / 2;
  const drawY = rect.y + (rect.height - drawH) / 2;

  lastPage.drawImage(pngImage, {
    x: drawX,
    y: drawY,
    width: drawW,
    height: drawH,
  });

  // Flatten the form to lock in values and signature
  form.flatten();

  // Add a new page for signature verification matching the template size

  const { width: pageWidth, height: pageHeight } = pages[0].getSize();
  const verificationPage = pdfDoc.addPage([pageWidth, pageHeight]);
  const vW = pageWidth;
  const vH = pageHeight;

  // Embed & draw logo and border on verification page only
  const logoBytes = await fetch(logoUrl).then(r => r.arrayBuffer());
  const logoImage = await pdfDoc.embedPng(logoBytes);
  const logoDims = logoImage.scale(0.1);
  // Logo at top-center
  verificationPage.drawImage(logoImage, {
    x: (vW - logoDims.width) / 2,
    y: vH - logoDims.height - 30,
    width: logoDims.width,
    height: logoDims.height,
  });
  // Border around page
  verificationPage.drawRectangle({
    x: 20,
    y: 20,
    width: vW - 40,
    height: vH - 40,
    borderColor: rgb(0.2, 0.2, 0.2),
    borderWidth: 1,
  });

  // Text lines for verification page
  const textLines = [
    `Signed by: ${values.debtor_full_name}`,
    `Debtor Email: ${values.debtor_email.toLowerCase()}`,
    `Debtor ID: ${values.debtor_id}`,
    `Agreement Date: ${values.date_signed}`,
    `Signed At: ${meta.date_signed_full}`,
    `Transaction ID: ${values.transaction_id}`,
    `IP Address: ${meta.ip_address}`,
    `User Agent: ${meta.user_agent}`,
  ];

  const headerFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
  const headerSize = 16;
  const headerText = 'Electronic Signature Verification';
  const textWidth = headerFont.widthOfTextAtSize(headerText, headerSize);
  const headerX = (vW - textWidth) / 2;
  let y = vH - logoDims.height - 60;
  // Draw centered header
  verificationPage.drawText(headerText, {
    x: headerX,
    y,
    size: headerSize,
    font: headerFont,
    color: rgb(0,0,0),
  });
  y -= headerSize + 20;
  // Switch to body font
  const bodyFont = await pdfDoc.embedFont(StandardFonts.TimesRoman);
  const bodySize = 10;
  const margin = 50;
  textLines.forEach(line => {
    verificationPage.drawText(line, {
      x: margin,
      y,
      size: bodySize,
      font: bodyFont,
      color: rgb(0,0,0),
      maxWidth: vW - margin * 2,
      lineHeight: bodySize + 4,
    });
    y -= bodySize + 8;
  });

  // Return the raw PDF bytes for upload/download
  return await pdfDoc.save();
}

export default function RetainerAgreementPage({ caseId }) {
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [chapter, setChapter] = useState(null);
  const [preFilingFees, setPreFilingFees] = useState(null);
  const [feesPaidPrior, setFeesPaidPrior] = useState(null);
  const [totalAttorneyFees, setTotalAttorneyFees] = useState(null);
  const [filingFeeAmount, setFilingFeeAmount] = useState(null);
  const [debtorFullName, setDebtorFullName] = useState('');
  const [debtorEmail, setDebtorEmail] = useState('');
  const [debtorId, setDebtorId] = useState(null);
  // For the retainer PDF: only the date
  const [dateAgreement] = useState(
    new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  );

  // For verification page: full date, time, and timezone
  const [dateSignedFull] = useState(
    new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    })
  );
  const sigPadRef = useRef(null);
  const submissionInProgressRef = useRef(false);
  const [retainerAgreements, setRetainerAgreements] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;
  const [showPayment, setShowPayment] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const navigate = useNavigate();
  const activeCaseId = caseId || localStorage.getItem('clientCaseId');
  // Down payment requirement: $500 for Ch7, half of fees but for Ch 13

  // Helper to normalize emails for comparison
  const normEmail = (e) => (e || '').trim().toLowerCase();

  // Determine if the logged-in debtor has already signed
  // (Some API responses may return `debtor_email` while others return `email`.)
  const myStatusById = statusList.find(d => (
    debtorId != null && String(d.debtor_id) === String(debtorId)
  ));
  const myStatus = myStatusById || statusList.find(d => {
    const rowEmail = normEmail(d.email || d.debtor_email);
    return rowEmail && rowEmail === normEmail(debtorEmail);
  });
  const hasSigned = myStatus?.signed;

  // Debug logging for case-sensitive / whitespace issues
  useEffect(() => {
    // Only log when we actually have data to compare
    if (!debtorEmail && (!statusList || statusList.length === 0)) return;

    const me = normEmail(debtorEmail);
    const rows = (statusList || []).map(d => {
      const raw = (d.email || d.debtor_email || '');
      return {
        debtor_id: d.debtor_id,
        debtor_full_name: d.debtor_full_name,
        rawEmail: raw,
        normEmail: normEmail(raw),
        signed: d.signed,
        matchesMe: normEmail(raw) === me,
      };
    });

    // eslint-disable-next-line no-console
    console.group('[RetainerAgreementPage] Retainer status email match debug');
    // eslint-disable-next-line no-console
    console.log('debtorEmail raw:', debtorEmail);
    // eslint-disable-next-line no-console
    console.log('debtorEmail normalized:', me);
    // eslint-disable-next-line no-console
    console.log('statusList length:', (statusList || []).length);
    // eslint-disable-next-line no-console
    console.table(rows);
    // eslint-disable-next-line no-console
    console.log('myStatus:', myStatus);
    // eslint-disable-next-line no-console
    console.log('hasSigned:', hasSigned);
    // eslint-disable-next-line no-console
    console.groupEnd();
  }, [debtorEmail, statusList]);

  useEffect(() => {
    fetch(`${API_URL}/user/tutorial-seen/`, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem("clientToken")}`,
      },
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => {
        setChapter(data.chapter);
        setPreFilingFees(data.pre_filing_fees);
        setFeesPaidPrior(data.fees_paid_prior);
        setTotalAttorneyFees(data.total_attorney_fees);
        setFilingFeeAmount(data.filing_fee_amount);
        setDebtorFullName(data.debtor_full_name);
        setDebtorEmail(normEmail(data.debtor_email));
        setDebtorId(data.debtor_id);
        // Compute required down payment from fresh API data:

        const pre = Number(data.pre_filing_fees || 0);
        let requiredDownPayment = 0;

        if (pre <= 0) {
          requiredDownPayment = 0;
        } else if (pre >= 1000) {
          requiredDownPayment = 500;
        } else {
          requiredDownPayment = Math.max(100, pre / 2);
          requiredDownPayment = Math.min(requiredDownPayment, pre);
        }
        // build values for preview
        const previewValues = {
          case_id: data.case_id,
          pre_filing_fees: data.pre_filing_fees,
          filing_fee_amount: data.filing_fee_amount,
          down_payment: requiredDownPayment,
          date_signed: dateAgreement,
          debtor_full_name: data.debtor_full_name,
          post_filing_fees: data.chapter === '13' ? data.total_attorney_fees - data.pre_filing_fees : null,
        };
        const templatePath = data.chapter === '7' ? '/docs/retainer_ch7.pdf' : '/docs/retainer_ch13.pdf';
        // Generate inline preview
        generatePreview(templatePath, previewValues)
          .then(url => setPreviewUrl(url))
          .catch(() => setPreviewUrl(null));
        // now fetch existing agreements
        fetch(`${API_URL}/case/${activeCaseId}/retainer-agreement/`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            Authorization: `Bearer ${localStorage.getItem("clientToken")}`,
          },
        })
          .then(res => res.json())
          .then(list => setRetainerAgreements(list))
          .catch(() => setRetainerAgreements([]))
          .then(() => {
            // Fetch signing status for all debtors
            fetch(`${API_URL}/case/${activeCaseId}/retainer-status/`, {
              method: 'GET',
              credentials: 'include',
              headers: {
                Authorization: `Bearer ${localStorage.getItem("clientToken")}`,
              },
            })
              .then(res => res.json())
              .then(list => setStatusList(list))
              .catch(() => setStatusList([]));
          });
      });
  }, [API_URL, activeCaseId, dateAgreement]);
      const pre = Number(preFilingFees || 0);
      let requiredDownPayment = 0;

      if (pre <= 0) {
        requiredDownPayment = 0;
      } else if (pre >= 1000) {
        requiredDownPayment = 500;
      } else {
        requiredDownPayment = Math.max(100, pre / 2);
        requiredDownPayment = Math.min(requiredDownPayment, pre);
      }
    const handleClear = () => {
        sigPadRef.current.clear();
        setSignatureDataUrl(null);
      };


  const handleSave = async () => {
    if (submissionInProgressRef.current) return;
    if (!signatureDataUrl && (!sigPadRef.current || sigPadRef.current.isEmpty())) {
      alert('Please sign the agreement before saving it.');
      return;
    }

    submissionInProgressRef.current = true;
    setIsLoading(true);
    try {
      const signatureData =
        signatureDataUrl ||
        sigPadRef.current?.toDataURL('image/png');

      // Fetch user's IP address
      const ipRes = await fetch('https://api.ipify.org?format=json');
      const { ip: ip_address } = await ipRes.json();
      const user_agent = navigator.userAgent;

      // Generate custom transaction ID
      const transactionId = `RAG-${Date.now()}-${Math.random().toString(36).slice(2,8)}`;

      const values = {
        case_id: activeCaseId,
        pre_filing_fees: preFilingFees,
        filing_fee_amount: filingFeeAmount,
        down_payment: feesPaidPrior,
        date_signed: dateAgreement,           // only date for the agreement PDF
        debtor_full_name: debtorFullName,
        debtor_email: normEmail(debtorEmail),
        debtor_id: debtorId,
        post_filing_fees: chapter === '13' ? totalAttorneyFees - preFilingFees : null,
        transaction_id: transactionId,
      };

      const meta = {
        ip_address,
        user_agent,
        date_signed_full: dateSignedFull,     // full datetime for verification
      };

      const templateUrl =
        chapter === '7'
          ? '/docs/retainer_ch7.pdf'
          : '/docs/retainer_ch13.pdf';

      // Generate the signed PDF bytes
      const pdfBytes = await fillAndDownloadTemplate(templateUrl, values, signatureData, meta);

      // Build form data to upload the PDF and metadata
      const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
      const formData = new FormData();
      const uniqueId =
        (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
        uuidv4();
      formData.append('upload', pdfBlob, `retainer-${activeCaseId}-${uniqueId}.pdf`);
      formData.append('transaction_id', transactionId);
      formData.append('ip_address', meta.ip_address);
      formData.append('user_agent', meta.user_agent);

      // POST to upload endpoint
      const uploadResponse = await fetch(`${API_URL}/case/${activeCaseId}/retainer-agreement/`, {
        method: 'POST',
        credentials: 'include',
        headers: {
            Authorization: `Bearer ${localStorage.getItem("clientToken")}`,
          },
        body: formData,
      });
      const uploadResult = await uploadResponse.json().catch(() => ({}));

      // A retry that reaches the server after the first request was accepted is
      // still a successful outcome. The server refuses to create a duplicate.
      if (uploadResponse.status === 409 && uploadResult.code === 'retainer_already_signed') {
        setStatusList(current => current.map(item => (
          String(item.debtor_id) === String(debtorId)
            ? { ...item, signed: true }
            : item
        )));
        if (uploadResult.agreement) {
          setRetainerAgreements(current => (
            current.some(item => item.id === uploadResult.agreement.id)
              ? current
              : [uploadResult.agreement, ...current]
          ));
        }
        setShowSuccessModal(true);
        return;
      }
      if (!uploadResponse.ok) {
        throw new Error(uploadResult.detail || 'The signed agreement could not be saved.');
      }

      setRetainerAgreements(current => [uploadResult, ...current]);
      setStatusList(current => current.map(item => (
        String(item.debtor_id) === String(debtorId)
          ? { ...item, signed: true }
          : item
      )));

      // Show custom success modal
      setShowSuccessModal(true);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Failed to generate and save the signed PDF.');
    } finally {
      submissionInProgressRef.current = false;
      setIsLoading(false);
    }
  };

  const handleSignClick = () => {
    if (isLoading || submissionInProgressRef.current) return;
    if (feesPaidPrior < requiredDownPayment) {
      console.log(feesPaidPrior)
      console.log(requiredDownPayment)
      setShowPayment(true);
    } else {
      console.log(feesPaidPrior)
      console.log(requiredDownPayment)
      handleSave();
    }
  };

  return (
    <div className="">
      <h1 className="text-2xl font-bold mb-4">Retainer Agreement</h1>
      {hasSigned ? (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded">
          <p className="text-center text-green-800 font-medium">
            {debtorFullName}, you have already signed your retainer agreement. Thank you and we look forward to working to get your Bankruptcy relief.
          </p>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6">
          {/* PDF Viewer */}
          <div className="flex-1">
            {previewUrl ? (
              <>
                <iframe
                  src={previewUrl}
                  title="Retainer Agreement"
                  className="w-full h-[600px] border rounded"
                />
                <div className="mt-2 text-right">
                  <a
                    href={previewUrl}
                    download={`retainer-${activeCaseId}.pdf`}
                    className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                  >
                    Download Full Agreement
                  </a>
                </div>
              </>
            ) : (
              <p className="text-center text-gray-500">Loading agreement preview&hellip;</p>
            )}
          </div>

          {/* Signature Form or Payment Portal */}
          <div className="flex-1">
            {showPayment ? (
              <PaymentPortal
                caseId={activeCaseId}
                amount={requiredDownPayment - feesPaidPrior}
                readOnlyAmount={true}
                onSuccess={() => {
                  setShowPayment(false);
                  handleSave();
                }}
              />
            ) : (
              <div className="bg-white rounded-lg shadow">
                <div className="p-4">
                  <label className="block font-medium mb-2">Signature</label>
                  <SignaturePad
                    ref={sigPadRef}
                    canvasProps={{ className: 'border rounded w-full h-20' }}
                    onEnd={() => {
                      const dataUrl = sigPadRef.current.toDataURL('image/png');
                      setSignatureDataUrl(dataUrl);
                    }}
                  />
                  <div className="mt-2 flex space-x-2">
                    <button
                      onClick={handleClear}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded"
                    >
                      Clear
                    </button>
                    {isLoading ? (
                      <button disabled className="px-4 py-2 bg-indigo-600 text-white rounded opacity-75 flex items-center space-x-2">
                        <svg className="w-5 h-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                        </svg>
                        <span>Loading...</span>
                      </button>
                    ) : (
                      <button
                        onClick={handleSignClick}
                        className="px-4 py-2 bg-indigo-600 text-white rounded"
                      >
                        Sign & Save
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Agreements still needed */}
      {statusList.some(d => !d.signed) && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded">
          <h3 className="text-lg font-semibold text-red-700">Retainer Agreements still needed</h3>
          <ul className="list-disc list-inside mt-2 text-red-700">
            {statusList.filter(d => !d.signed).map(d => {
              const rowEmail = normEmail(d.email || d.debtor_email);
              const isMe = debtorId != null
                ? String(d.debtor_id) === String(debtorId)
                : rowEmail && rowEmail === normEmail(debtorEmail);

              return (
                <li key={d.debtor_id}>
                  {isMe
                    ? `${d.debtor_full_name}: Sign the agreement above.`
                    : `${d.debtor_full_name}: they must login to their portal using email `}

                  {!isMe && (
                    <span className="font-medium">{rowEmail}</span>
                  )}

                  {!isMe && ' to complete and sign the retainer agreement.'}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {/* List of previous agreements */}
      <div className="mt-8">
        <h2 className="text-xl font-semibold mb-2">Signed Retainer Agreements</h2>
        {retainerAgreements.length === 0 ? (
          <p className="text-gray-500">No agreements uploaded yet.</p>
        ) : (
          <ul className="space-y-2">
            {retainerAgreements.map(rec => (
              <li key={rec.id} className="flex items-center space-x-4">
                <a
                  href={rec.upload_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                >
                  {rec.filename}
                </a>
                <span className="text-gray-500 text-sm">
                  {new Date(rec.created_at).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    {/* Success Modal */}
    {showSuccessModal && (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full text-center space-y-4">
          {/* Centered logo */}
          <img
            src="/images/logo.png"
            alt="Cisowski Law Logo"
            className="mx-auto h-12 mb-2"
          />
          {/* Centered thank you header */}
          <h2 className="text-2xl font-bold text-gray-800">
            Thank you for retaining Cisowski Law
          </h2>
          {/* Styled message */}
          <p className="text-gray-600">
            We look forward to getting you your Bankruptcy relief!
          </p>
          <p className="text-gray-600 mt-2">
            Refresh your browser to review and download the signed retainer agreement or
          </p>
          {/* Continue button only */}
          <button
            onClick={() => navigate('/client/dashboard/oneview')}
            className="mt-4 w-full px-6 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          >
            Continue to Credit Report
          </button>
        </div>
      </div>
    )}
    </div>
  );
}
